import React, { useState, useEffect } from "react";
import { ScenarioRunProto } from "build-ts/schedule";
import { fetchChatSession } from "services/AgentChatService";
import { ChatMessageType } from "build-ts/common";
import { ChatResponse } from "build-ts/chat_session";
import AnalysisScenarioInsightsCardComponent from "modules/analyst/components/AnalysisScenarioInsightsCard";

// Cache chat responses to avoid duplicate API calls across card renders
const chatResponseCache = new Map<string, ChatResponse | null>();

interface AnalysisScenarioInsightsCardContainerProps {
  scenarioRun?: ScenarioRunProto;
}

const AnalysisScenarioInsightsCardContainer: React.FC<AnalysisScenarioInsightsCardContainerProps> = ({
  scenarioRun,
}) => {
  const [chatResponse, setChatResponse] = useState<ChatResponse | null | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const sessionId = scenarioRun?.analyst_chat_session_id;

  useEffect(() => {
    if (!sessionId) return;

    if (chatResponseCache.has(sessionId)) {
      setChatResponse(chatResponseCache.get(sessionId));
      return;
    }

    setIsLoading(true);
    fetchChatSession(sessionId, false)
      .then((response) => {
        const value = response ?? null;
        chatResponseCache.set(sessionId, value);
        setChatResponse(value);
      })
      .catch(() => {
        chatResponseCache.set(sessionId, null);
        setChatResponse(null);
      })
      .finally(() => setIsLoading(false));
  }, [sessionId]);

  if (!sessionId) {
    return null;
  }

  const lastAiContent = chatResponse
    ? [...(chatResponse.messages || [])]
      .reverse()
      .find((m) => m.message_type === ChatMessageType.RESPONSE)
      ?.content
    : undefined;

  return (
    <AnalysisScenarioInsightsCardComponent
      isLoading={isLoading}
      lastAiContent={lastAiContent}
    />
  );
};

export default AnalysisScenarioInsightsCardContainer;
